/* ---------------------------------------------------------------------------
   The recorder's public surface: phases, state, events, errors.

   Split out from recorder.ts so the engine file stays readable. Everything here
   is re-exported by ./recorder — import from there, not from this file.
   --------------------------------------------------------------------------- */

import type { SubChunk } from "../../convex/lib/game";

export type RecorderPhase =
  | "idle"
  | "preparing"
  | "ready"
  | "refresher"
  | "countdown"
  | "recording"
  | "complete"
  | "error";

export interface RecorderState {
  phase: RecorderPhase;
  /** -1 before the first sub-chunk starts. */
  subChunkIndex: number;
  subChunkCount: number;
  /** Seconds elapsed within the current phase. */
  elapsed: number;
  /** Seconds left in the current phase. */
  remaining: number;
  /** 0..1 through the current phase. */
  progress: number;
  /** Smoothed mic level, 0..1. Drive the input meter with this. */
  level: number;
  /** Instantaneous peak amplitude, 0..1. */
  peak: number;
  /** 3, 2, 1 during the countdown; 0 in every other phase. */
  countdownRemaining: number;
  isRedo: boolean;
  redosRemaining: number;
  recordedCount: number;
  /** The MediaRecorder container actually in use. Playback needs this. */
  mimeType: string | null;
}

export interface SubChunkRecording {
  index: number;
  subChunk: SubChunk;
  blob: Blob;
  /** Exactly what MediaRecorder produced — send this as the upload's type. */
  mimeType: string;
  sizeBytes: number;
  durationMs: number;
  recordedAt: number;
  isRedo: boolean;
}

export type RecorderEvent =
  | { type: "state"; state: RecorderState }
  | { type: "phase"; phase: RecorderPhase; previous: RecorderPhase; state: RecorderState }
  | { type: "countdown"; subChunkIndex: number; secondsRemaining: number }
  | { type: "subchunk"; recording: SubChunkRecording; state: RecorderState }
  | { type: "complete"; recordings: SubChunkRecording[] }
  | { type: "error"; error: RecorderError };

export type RecorderListener = (event: RecorderEvent) => void;

export type RecorderErrorCode =
  | "unsupported"
  | "mic-denied"
  | "mic-unavailable"
  | "playback-blocked"
  | "recorder-failed"
  | "no-audio-captured"
  | "invalid-state";

export class RecorderError extends Error {
  readonly code: RecorderErrorCode;
  constructor(code: RecorderErrorCode, message: string, cause?: unknown) {
    super(message);
    this.name = "RecorderError";
    this.code = code;
    if (cause !== undefined) this.cause = cause;
  }
}

export interface DubRecorderOptions {
  /** Absolute seconds within the source clip. */
  segmentStart: number;
  segmentLength: number;
  /** Defaults to SUBCHUNK_SEC from convex/lib/game. */
  subChunkSec?: number;
  /** Defaults to COUNTDOWN_SEC from convex/lib/game. */
  countdownSec?: number;
  /** Defaults to REDOS_PER_SEGMENT from convex/lib/game. */
  redosAllowed?: number;
  /** Cap on how often `state` events fire, in ms. Default 50. */
  stateThrottleMs?: number;
}
